<template>
  <v-container fluid class="pa-4">
    <!-- ✅ HEADER PRINCIPAL -->
    <div class="page-header mb-4">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="pink-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-shield-lock</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Gerenciar Permissões</h1>
              <div class="text-caption text-grey">
                Configure permissões detalhadas por grupo e aplicação
              </div>
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="deep-purple-darken-1"
              variant="flat"
              @click="voltarParaGrupos"
              class="text-none mr-2"
              prepend-icon="mdi-account-group"
            >
              Ver Grupos
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-card class="mb-4">
      <v-card-title class="d-flex align-center pa-4 bg-grey-lighten-4">
        <v-icon left>mdi-cog</v-icon>
        <span class="text-h6 ml-2">Configuração de Permissões</span>
      </v-card-title>

      <!-- Seletor de Grupo -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="grupoSelecionado"
              :items="grupos"
              item-title="description"
              item-value="group_id"
              label="Selecione um Grupo"
              prepend-icon="mdi-account-multiple"
              :loading="carregandoGrupos"
              @update:modelValue="carregarPermissoes"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabela de Permissões -->
    <v-card v-if="grupoSelecionado">
      <v-card-title class="d-flex align-center pa-4 bg-grey-lighten-4">
        <v-icon left>mdi-table</v-icon>
        <span class="text-h6 ml-2">Permissões do Grupo</span>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-3">
          <!-- Busca -->
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Busque por descrição ou app_name..."
              hint="Digite e aguarde 0.5s ou pressione Enter"
              persistent-hint
              variant="outlined"
              density="compact"
              clearable
              @keydown.enter="buscarImediatamente"
            ></v-text-field>
          </v-col>

          <!-- Tipo -->
          <v-col cols="12" md="2">
            <v-select
              v-model="filtroTipo"
              :items="tiposDisponiveis"
              label="Tipo"
              prepend-inner-icon="mdi-filter"
              hint="Ex: menu, api, categoria"
              persistent-hint
              variant="outlined"
              density="compact"
              clearable
            ></v-select>
          </v-col>

          <!-- Categoria (Pai) -->
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroPai"
              :items="paisDisponiveis"
              item-title="label"
              item-value="value"
              label="Categoria"
              prepend-inner-icon="mdi-folder"
              hint="Filtre por módulo do sistema"
              persistent-hint
              variant="outlined"
              density="compact"
              clearable
            ></v-select>
          </v-col>

          <!-- Botão Limpar -->
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn
              color="grey"
              variant="tonal"
              block
              @click="limparFiltros"
            >
              <v-icon start size="small">mdi-filter-off</v-icon>
              Limpar Filtros
            </v-btn>
          </v-col>

          <!-- Linha 2: Datas -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model="dataInicial"
              label="Data Inicial"
              type="date"
              prepend-inner-icon="mdi-calendar-start"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="dataFinal"
              label="Data Final"
              type="date"
              prepend-inner-icon="mdi-calendar-end"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Expansion Panels Agrupados por Categoria -->
        <v-expansion-panels v-model="panelsAbertos" multiple class="mb-4" v-if="grupoSelecionado && permissoes.length > 0">
          <v-expansion-panel
            v-for="grupo in permissoesAgrupadas"
            :key="grupo.categoria"
            elevation="2"
          >
            <v-expansion-panel-title class="bg-grey-lighten-4">
              <v-row align="center" no-gutters>
                <v-col cols="auto">
                  <v-icon color="primary" class="mr-2">
                    {{ grupo.icon }}
                  </v-icon>
                  <span class="font-weight-bold text-h6">{{ grupo.nome }}</span>
                </v-col>
                <v-col cols="auto" class="ml-4">
                  <v-chip size="small" color="primary" variant="tonal">
                    {{ grupo.apps.length }} apps
                  </v-chip>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="d-flex gap-2" @click.stop>
                  <v-btn
                    size="x-small"
                    color="success"
                    variant="tonal"
                    @click.stop="marcarTodasCategoria(grupo.categoria)"
                  >
                    <v-icon size="small">mdi-checkbox-marked</v-icon>
                    Marcar Todas
                  </v-btn>
                  <v-btn
                    size="x-small"
                    color="error"
                    variant="tonal"
                    @click.stop="desmarcarTodasCategoria(grupo.categoria)"
                  >
                    <v-icon size="small">mdi-checkbox-blank-outline</v-icon>
                    Desmarcar Todas
                  </v-btn>
                  <v-btn
                    size="x-small"
                    :color="visualizacaoCompleta ? 'primary' : 'grey'"
                    variant="tonal"
                    @click.stop="visualizacaoCompleta = !visualizacaoCompleta"
                    :title="visualizacaoCompleta ? 'Visualização Completa' : 'Visualização Básica'"
                  >
                    <v-icon size="small">{{ visualizacaoCompleta ? 'mdi-table-large' : 'mdi-table' }}</v-icon>
                    {{ visualizacaoCompleta ? 'Completa' : 'Básica' }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-data-table
                :headers="headersTabela"
                :items="grupo.apps"
                density="compact"
                :items-per-page="-1"
                hide-default-footer
              >
                <!-- Descrição / Aplicação -->
                <template #[`item.description`]="{ item }">
                  <v-tooltip location="bottom">
                    <template #activator="{ props }">
                      <div v-bind="props">
                        <div v-if="item.description" class="text-body-2">
                          {{ item.description }}
                        </div>
                        <div v-else class="font-weight-bold font-monospace text-body-2 text-grey-darken-2">
                          {{ item.app_name }}
                        </div>
                      </div>
                    </template>
                    <div class="text-caption">
                      <strong>App Name:</strong> {{ item.app_name }}<br>
                      <span v-if="item.app_type"><strong>Tipo:</strong> {{ item.app_type }}<br></span>
                      <span v-if="item.parent_app"><strong>Pai:</strong> {{ item.parent_app }}</span>
                    </div>
                  </v-tooltip>
                </template>

                <!-- Nome da Aplicação -->
                <template #[`item.app_name`]="{ item }">
                  <div class="font-weight-bold text-body-2 font-monospace">
                    {{ item.app_name }}
                  </div>
                </template>

                <!-- Tipo -->
                <template #[`item.app_type`]="{ item }">
                  <v-chip size="x-small" :color="getTypeColor(item.app_type)">
                    {{ item.app_type || 'N/A' }}
                  </v-chip>
                </template>

                <!-- Pai -->
                <template #[`item.parent_app`]="{ item }">
                  <div class="text-caption font-monospace" v-if="item.parent_app">
                    {{ item.parent_app }}
                  </div>
                  <div class="text-caption text-grey" v-else>
                    -
                  </div>
                </template>

                <!-- Permissões com Checkboxes -->
                <template #[`item.priv_access`]="{ item }">
                  <v-checkbox
                    v-model="item.priv_access"
                    true-value="Y"
                    false-value="N"
                    color="success"
                    hide-details
                    density="compact"
                    @change="atualizarPermissao(item, 'priv_access')"
                  ></v-checkbox>
                </template>

                <template #[`item.priv_insert`]="{ item }">
                  <!-- 🔒 Ocultar se tipo = 'acesso' -->
                  <v-checkbox
                    v-if="item.app_type !== 'acesso'"
                    v-model="item.priv_insert"
                    true-value="Y"
                    false-value="N"
                    color="primary"
                    hide-details
                    density="compact"
                    :disabled="item.priv_access !== 'Y'"
                    @change="atualizarPermissao(item, 'priv_insert')"
                  ></v-checkbox>
                  <div v-else class="text-caption text-grey">-</div>
                </template>

                <template #[`item.priv_update`]="{ item }">
                  <!-- 🔒 Ocultar se tipo = 'acesso' -->
                  <v-checkbox
                    v-if="item.app_type !== 'acesso'"
                    v-model="item.priv_update"
                    true-value="Y"
                    false-value="N"
                    color="info"
                    hide-details
                    density="compact"
                    :disabled="item.priv_access !== 'Y'"
                    @change="atualizarPermissao(item, 'priv_update')"
                  ></v-checkbox>
                  <div v-else class="text-caption text-grey">-</div>
                </template>

                <template #[`item.priv_delete`]="{ item }">
                  <!-- 🔒 Ocultar se tipo = 'acesso' -->
                  <v-checkbox
                    v-if="item.app_type !== 'acesso'"
                    v-model="item.priv_delete"
                    true-value="Y"
                    false-value="N"
                    color="error"
                    hide-details
                    density="compact"
                    :disabled="item.priv_access !== 'Y'"
                    @change="atualizarPermissao(item, 'priv_delete')"
                  ></v-checkbox>
                  <div v-else class="text-caption text-grey">-</div>
                </template>

                <template #[`item.priv_export`]="{ item }">
                  <!-- 🔒 Ocultar se tipo = 'acesso' -->
                  <v-checkbox
                    v-if="item.app_type !== 'acesso'"
                    v-model="item.priv_export"
                    true-value="Y"
                    false-value="N"
                    color="orange"
                    hide-details
                    density="compact"
                    :disabled="item.priv_access !== 'Y'"
                    @change="atualizarPermissao(item, 'priv_export')"
                  ></v-checkbox>
                  <div v-else class="text-caption text-grey">-</div>
                </template>

                <template #[`item.priv_print`]="{ item }">
                  <!-- 🔒 Ocultar se tipo = 'acesso' -->
                  <v-checkbox
                    v-if="item.app_type !== 'acesso'"
                    v-model="item.priv_print"
                    true-value="Y"
                    false-value="N"
                    color="purple"
                    hide-details
                    density="compact"
                    :disabled="item.priv_access !== 'Y'"
                    @change="atualizarPermissao(item, 'priv_print')"
                  ></v-checkbox>
                  <div v-else class="text-caption text-grey">-</div>
                </template>
              </v-data-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Mensagem quando não há permissões -->
        <v-alert
          v-else-if="grupoSelecionado && permissoes.length === 0"
          type="info"
          variant="tonal"
          class="mt-4"
        >
          <v-icon start>mdi-information</v-icon>
          Nenhuma permissão encontrada para este grupo.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Mensagem quando nenhum grupo está selecionado -->
    <v-card v-else>
      <v-card-text class="text-center py-10">
        <v-icon size="80" color="grey lighten-2">mdi-shield-lock-outline</v-icon>
        <div class="text-h6 grey--text mt-4">
          Selecione um grupo para gerenciar suas permissões
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import permissionsService from '@/services/permissionsService';
import { useToast } from 'vue-toastification';

export default {
  name: 'GerenciarPermissoes',

  data() {
    return {
      grupos: [],
      permissoes: [],
      grupoSelecionado: null,
      carregandoGrupos: false,
      carregandoPermissoes: false,
      search: '',
      searchDebounced: '',
      searchTimeout: null,
      filtroTipo: null,
      filtroPai: null,
      dataInicial: null,
      dataFinal: null,
      panelsAbertos: [],
      visualizacaoCompleta: false
    };
  },

  computed: {
    toast() {
      return useToast();
    },

    // Headers dinâmicos baseados no modo de visualização
    headersTabela() {
      const headersBasicos = [
        { title: 'Descrição / Aplicação', key: 'description', sortable: true, width: '700px' },
        { title: 'Acesso', key: 'priv_access', sortable: false, align: 'center', width: '70px' },
        { title: 'Inserir', key: 'priv_insert', sortable: false, align: 'center', width: '70px' },
        { title: 'Editar', key: 'priv_update', sortable: false, align: 'center', width: '70px' },
        { title: 'Deletar', key: 'priv_delete', sortable: false, align: 'center', width: '70px' },
        { title: 'Exportar', key: 'priv_export', sortable: false, align: 'center', width: '80px' },
        { title: 'Imprimir', key: 'priv_print', sortable: false, align: 'center', width: '80px' }
      ];

      const headersCompletos = [
        { title: 'Descrição', key: 'description', sortable: true, width: '250px' },
        { title: 'Aplicação', key: 'app_name', sortable: true },
        { title: 'Tipo', key: 'app_type', sortable: true, align: 'center', width: '80px' },
        { title: 'Pai', key: 'parent_app', sortable: true, align: 'center', width: '150px' },
        { title: 'Acesso', key: 'priv_access', sortable: false, align: 'center', width: '70px' },
        { title: 'Inserir', key: 'priv_insert', sortable: false, align: 'center', width: '70px' },
        { title: 'Editar', key: 'priv_update', sortable: false, align: 'center', width: '70px' },
        { title: 'Deletar', key: 'priv_delete', sortable: false, align: 'center', width: '70px' },
        { title: 'Exportar', key: 'priv_export', sortable: false, align: 'center', width: '80px' },
        { title: 'Imprimir', key: 'priv_print', sortable: false, align: 'center', width: '80px' }
      ];

      return this.visualizacaoCompleta ? headersCompletos : headersBasicos;
    },

    // Lista de tipos disponíveis (extraindo dos dados)
    tiposDisponiveis() {
      const tipos = [...new Set(this.permissoes.map(p => p.app_type).filter(t => t))];
      return tipos.sort();
    },

    // Lista de categorias (pais) disponíveis
    paisDisponiveis() {
      const categoriasInfo = {
        'menu_producao_geral': { label: '🏭 Produção', ordem: 1 },
        'menu_planejamento_geral': { label: '📅 Planejamento', ordem: 2 },
        'menu_maquinas_geral': { label: '⚙️ Máquinas & Equipamentos', ordem: 3 },
        'menu_expediente_geral': { label: '🕐 Expediente & RH', ordem: 4 },
        'menu_suporte_geral': { label: '🎧 Suporte & Comunicação', ordem: 5 },
        'menu_administracao_geral': { label: '🛡️ Administração', ordem: 6 },
        'menu_usuario_geral': { label: '👥 Usuários', ordem: 7 }
      };

      const pais = [...new Set(this.permissoes.map(p => p.parent_app).filter(p => p))];
      
      const resultado = pais.map(pai => ({
        value: pai,
        label: categoriasInfo[pai]?.label || pai,
        ordem: categoriasInfo[pai]?.ordem || 999
      })).sort((a, b) => a.ordem - b.ordem);

      // Adicionar opção "Sem Categoria"
      return [
        { value: '__sem_pai__', label: '📱 Outras Aplicações', ordem: 1000 },
        ...resultado
      ];
    },

    // Permissões filtradas por tipo e data
    permissoesFiltradas() {
      let filtradas = this.permissoes;

      // Filtrar por busca (searchDebounced)
      if (this.searchDebounced && this.searchDebounced.trim() !== '') {
        const termo = this.searchDebounced.toLowerCase();
        filtradas = filtradas.filter(p => 
          p.app_name.toLowerCase().includes(termo) ||
          (p.description && p.description.toLowerCase().includes(termo))
        );
      }

      // Filtrar por tipo
      if (this.filtroTipo) {
        filtradas = filtradas.filter(p => p.app_type === this.filtroTipo);
      }

      // Filtrar por pai (categoria)
      if (this.filtroPai) {
        if (this.filtroPai === '__sem_pai__') {
          filtradas = filtradas.filter(p => !p.parent_app);
        } else {
          filtradas = filtradas.filter(p => p.parent_app === this.filtroPai);
        }
      }

      // Filtrar por data inicial
      if (this.dataInicial) {
        filtradas = filtradas.filter(p => {
          if (!p.created_at) return false;
          const dataApp = new Date(p.created_at).toISOString().split('T')[0];
          return dataApp >= this.dataInicial;
        });
      }

      // Filtrar por data final
      if (this.dataFinal) {
        filtradas = filtradas.filter(p => {
          if (!p.created_at) return false;
          const dataApp = new Date(p.created_at).toISOString().split('T')[0];
          return dataApp <= this.dataFinal;
        });
      }

      return filtradas;
    },

    // Agrupar permissões por categoria pai
    permissoesAgrupadas() {
      if (this.permissoesFiltradas.length === 0) {
        return [];
      }

      const grupos = {};
      const semPai = [];

      // Primeiro, separar apps com e sem pai
      this.permissoesFiltradas.forEach(app => {
        if (app.parent_app) {
          if (!grupos[app.parent_app]) {
            grupos[app.parent_app] = [];
          }
          grupos[app.parent_app].push(app);
        } else {
          semPai.push(app);
        }
      });

      // Mapear categorias conhecidas com ícones e ordem
      const categoriasInfo = {
        'menu_producao_geral': { nome: 'Produção', icon: 'mdi-factory', ordem: 1 },
        'menu_planejamento_geral': { nome: 'Planejamento', icon: 'mdi-calendar-clock', ordem: 2 },
        'menu_maquinas_geral': { nome: 'Máquinas & Equipamentos', icon: 'mdi-cog', ordem: 3 },
        'menu_expediente_geral': { nome: 'Expediente & RH', icon: 'mdi-clock', ordem: 4 },
        'menu_suporte_geral': { nome: 'Suporte & Comunicação', icon: 'mdi-headset', ordem: 5 },
        'menu_administracao_geral': { nome: 'Administração', icon: 'mdi-shield-account', ordem: 6 },
        'menu_usuario_geral': { nome: 'Usuários', icon: 'mdi-account-group', ordem: 7 }
      };

      // Criar array de grupos
      const resultado = [];

      // Adicionar categorias conhecidas ordenadas
      const categoriasOrdenadas = Object.keys(grupos).sort((a, b) => {
        const ordemA = categoriasInfo[a]?.ordem || 999;
        const ordemB = categoriasInfo[b]?.ordem || 999;
        return ordemA - ordemB;
      });

      categoriasOrdenadas.forEach(categoria => {
        const info = categoriasInfo[categoria] || { 
          nome: categoria, 
          icon: 'mdi-folder',
          ordem: 999
        };
        
        resultado.push({
          categoria: categoria,
          nome: info.nome,
          icon: info.icon,
          apps: grupos[categoria].sort((a, b) => (a.order || 0) - (b.order || 0))
        });
      });

      // Se houver apps sem pai, adicionar como ÚLTIMO grupo
      if (semPai.length > 0) {
        resultado.push({
          categoria: '__sem_pai__',
          nome: 'Outras Aplicações',
          icon: 'mdi-application',
          apps: semPai.sort((a, b) => a.app_name.localeCompare(b.app_name))
        });
      }

      return resultado;
    }
  },

  watch: {
    grupoSelecionado(newValue) {
      if (newValue) {
        this.carregarPermissoes();
      } else {
        this.permissoes = [];
      }
    },

    // Debounce na busca - só pesquisa após parar de digitar
    search(newValue) {
      // Limpar timeout anterior
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Criar novo timeout de 500ms
      this.searchTimeout = setTimeout(() => {
        this.searchDebounced = newValue;
        
        // Abrir panels com resultados após debounce
        if (newValue && newValue.trim() !== '') {
          this.panelsAbertos = this.permissoesAgrupadas
            .map((grupo, index) => {
              const temResultado = grupo.apps.some(app => 
                app.app_name.toLowerCase().includes(newValue.toLowerCase()) ||
                (app.description && app.description.toLowerCase().includes(newValue.toLowerCase()))
              );
              return temResultado ? index : null;
            })
            .filter(index => index !== null);
        } else {
          // Busca vazia, limpar debounced também
          this.searchDebounced = '';
        }
      }, 500); // 500ms de espera
    },

    // Também observar mudanças nos agrupamentos
    permissoesAgrupadas() {
      // Se houver busca ativa (debounced), reabrir panels com resultados
      if (this.searchDebounced && this.searchDebounced.trim() !== '') {
        this.$nextTick(() => {
          this.panelsAbertos = this.permissoesAgrupadas
            .map((grupo, index) => {
              const temResultado = grupo.apps.some(app => 
                app.app_name.toLowerCase().includes(this.searchDebounced.toLowerCase()) ||
                (app.description && app.description.toLowerCase().includes(this.searchDebounced.toLowerCase()))
              );
              return temResultado ? index : null;
            })
            .filter(index => index !== null);
        });
      }
    }
  },

  mounted() {
    this.carregarGrupos();
    
    // Se foi passado um groupId na rota, seleciona ele
    if (this.$route.params.groupId) {
      this.grupoSelecionado = parseInt(this.$route.params.groupId);
      this.carregarPermissoes();
    }
  },

  methods: {
    voltarMenu() {
      this.$router.go(-1); // Volta para a página anterior
    },
    async carregarGrupos() {
      this.carregandoGrupos = true;
      try {
        const response = await permissionsService.listarGrupos();
        if (response.status === 'sucesso') {
          this.grupos = response.grupos;
        }
      } catch (error) {
        console.error('Erro ao carregar grupos:', error);
        this.toast.error('Erro ao carregar grupos');
      } finally {
        this.carregandoGrupos = false;
      }
    },

    async carregarPermissoes() {
      if (!this.grupoSelecionado) {
        this.permissoes = [];
        return;
      }

      this.carregandoPermissoes = true;
      this.permissoes = []; // Limpa a grid antes de carregar novos dados
      
      try {
        const response = await permissionsService.listarPermissoesGrupo(this.grupoSelecionado);
        if (response.status === 'sucesso') {
          this.permissoes = response.permissoes;
        }
      } catch (error) {
        console.error('Erro ao carregar permissões:', error);
        this.toast.error('Erro ao carregar permissões');
      } finally {
        this.carregandoPermissoes = false;
      }
    },

    // Marcar todas as permissões de uma categoria específica
    async marcarTodasCategoria(categoria) {
      if (!this.grupoSelecionado) return;

      const grupo = this.permissoesAgrupadas.find(g => g.categoria === categoria);
      if (!grupo) return;

      this.carregandoPermissoes = true;

      try {
        for (const app of grupo.apps) {
          app.priv_access = 'Y';
          app.priv_insert = 'Y';
          app.priv_update = 'Y';
          app.priv_delete = 'Y';
          app.priv_export = 'Y';
          app.priv_print = 'Y';

          await permissionsService.atualizarPermissao(
            this.grupoSelecionado,
            app.app_name,
            {
              priv_access: 'Y',
              priv_insert: 'Y',
              priv_delete: 'Y',
              priv_update: 'Y',
              priv_export: 'Y',
              priv_print: 'Y'
            }
          );
        }

        this.toast.success(`Todas as permissões de "${grupo.nome}" foram marcadas!`);
      } catch (error) {
        console.error('Erro ao marcar todas:', error);
        this.toast.error('Erro ao marcar permissões da categoria');
      } finally {
        this.carregandoPermissoes = false;
      }
    },

    // Desmarcar todas as permissões de uma categoria específica
    async desmarcarTodasCategoria(categoria) {
      if (!this.grupoSelecionado) return;

      const grupo = this.permissoesAgrupadas.find(g => g.categoria === categoria);
      if (!grupo) return;

      this.carregandoPermissoes = true;

      try {
        for (const app of grupo.apps) {
          app.priv_access = 'N';
          app.priv_insert = 'N';
          app.priv_update = 'N';
          app.priv_delete = 'N';
          app.priv_export = 'N';
          app.priv_print = 'N';

          await permissionsService.atualizarPermissao(
            this.grupoSelecionado,
            app.app_name,
            {
              priv_access: 'N',
              priv_insert: 'N',
              priv_delete: 'N',
              priv_update: 'N',
              priv_export: 'N',
              priv_print: 'N'
            }
          );
        }

        this.toast.success(`Todas as permissões de "${grupo.nome}" foram desmarcadas!`);
      } catch (error) {
        console.error('Erro ao desmarcar todas:', error);
        this.toast.error('Erro ao desmarcar permissões da categoria');
      } finally {
        this.carregandoPermissoes = false;
      }
    },

    // Selecionar todas as permissões das aplicações filtradas
    async selecionarTodos() {
      if (!this.grupoSelecionado) return;
      
      const confirmado = confirm(
        `Deseja dar TODAS as permissões (Acesso, Inserir, Editar, Deletar, Exportar, Imprimir) para as ${this.permissoesFiltradas.length} aplicações ${this.filtroTipo ? `do tipo "${this.filtroTipo}"` : ''}?`
      );
      
      if (!confirmado) return;

      this.carregandoPermissoes = true;
      
      try {
        for (const item of this.permissoesFiltradas) {
          item.priv_access = 'Y';
          item.priv_insert = 'Y';
          item.priv_delete = 'Y';
          item.priv_update = 'Y';
          item.priv_export = 'Y';
          item.priv_print = 'Y';
          
          await this.atualizarPermissao(item, 'all');
        }
        
        this.toast.success(`Todas as permissões foram habilitadas!`);
      } catch (error) {
        console.error('Erro ao selecionar todos:', error);
        this.toast.error('Erro ao selecionar todas as permissões');
      } finally {
        this.carregandoPermissoes = false;
      }
    },

    // Desmarcar todas as permissões das aplicações filtradas
    async desmarcarTodos() {
      if (!this.grupoSelecionado) return;
      
      const confirmado = confirm(
        `Deseja REMOVER TODAS as permissões das ${this.permissoesFiltradas.length} aplicações ${this.filtroTipo ? `do tipo "${this.filtroTipo}"` : ''}?`
      );
      
      if (!confirmado) return;

      this.carregandoPermissoes = true;
      
      try {
        for (const item of this.permissoesFiltradas) {
          item.priv_access = 'N';
          item.priv_insert = 'N';
          item.priv_delete = 'N';
          item.priv_update = 'N';
          item.priv_export = 'N';
          item.priv_print = 'N';
          
          await this.atualizarPermissao(item, 'all');
        }
        
        this.toast.success(`Todas as permissões foram removidas!`);
      } catch (error) {
        console.error('Erro ao desmarcar todos:', error);
        this.toast.error('Erro ao desmarcar todas as permissões');
      } finally {
        this.carregandoPermissoes = false;
      }
    },

    async atualizarPermissao(item, campo) {
      try {
        // Se marcou/desmarcou ACESSO, marca/desmarca todas as outras permissões
        if (campo === 'priv_access') {
          if (item.priv_access === 'N') {
            // Desmarcou: remove tudo
            item.priv_insert = 'N';
            item.priv_update = 'N';
            item.priv_delete = 'N';
            item.priv_export = 'N';
            item.priv_print = 'N';
          } else {
            // Marcou: marca tudo
            item.priv_insert = 'Y';
            item.priv_update = 'Y';
            item.priv_delete = 'Y';
            item.priv_export = 'Y';
            item.priv_print = 'Y';
          }
        }

        const dados = {
          priv_access: item.priv_access,
          priv_insert: item.priv_insert,
          priv_delete: item.priv_delete,
          priv_update: item.priv_update,
          priv_export: item.priv_export,
          priv_print: item.priv_print
        };

        const response = await permissionsService.atualizarPermissao(
          this.grupoSelecionado,
          item.app_name,
          dados
        );

        if (response.status === 'sucesso') {
          this.toast.success('Permissão atualizada');
        } else {
          this.toast.error('Erro ao atualizar permissão');
          // Reverter a mudança em caso de erro
          item[campo] = item[campo] === 'Y' ? 'N' : 'Y';
        }
      } catch (error) {
        console.error('Erro ao atualizar permissão:', error);
        this.toast.error('Erro ao atualizar permissão');
        // Reverter a mudança em caso de erro
        item[campo] = item[campo] === 'Y' ? 'N' : 'Y';
      }
    },

    buscarImediatamente() {
      // Limpar timeout se existir
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Aplicar busca imediatamente
      this.searchDebounced = this.search;
      
      // Abrir panels com resultados
      if (this.search && this.search.trim() !== '') {
        this.panelsAbertos = this.permissoesAgrupadas
          .map((grupo, index) => {
            const temResultado = grupo.apps.some(app => 
              app.app_name.toLowerCase().includes(this.search.toLowerCase()) ||
              (app.description && app.description.toLowerCase().includes(this.search.toLowerCase()))
            );
            return temResultado ? index : null;
          })
          .filter(index => index !== null);
      }
    },

    limparFiltros() {
      this.search = '';
      this.searchDebounced = '';
      this.filtroTipo = null;
      this.filtroPai = null;
      this.dataInicial = null;
      this.dataFinal = null;
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.toast.info('Filtros limpos');
    },

    getTypeColor(type) {
      const colors = {
        'web': 'blue',
        'api': 'green',
        'mobile': 'orange',
        'desktop': 'purple'
      };
      return colors[type] || 'grey';
    },

    voltarParaGrupos() {
      this.$router.push({ name: 'GerenciarGrupos' });
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

/* Destacar linha ao passar o mouse */
.v-data-table >>> tbody tr:hover {
  background-color: #e3f2fd !important;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

/* Destacar ainda mais quando está modificando */
.v-data-table >>> tbody tr:hover td {
  font-weight: 500;
}
</style>
